import type { DeliveryVariant } from "@/lib/image-delivery";
import { siteDay } from "@/lib/site-time";
import { naarPlatteTekst } from "@/lib/rich-text";
/**
 * Types en helpers voor blogartikels.
 * De inhoud zelf staat in de databank (tabel blog_posts) en wordt beheerd via /admin/artikels.
 */
import type { FaqItem } from "@/content/faqs";

export type Block =
  | { type: "p"; text: string }
  | { type: "list"; items: string[] }
  | { type: "steps"; items: { title: string; text: string }[] }
  | { type: "table"; head: string[]; rows: string[][] }
  | { type: "note"; text: string };

export type Section = {
  heading: string;
  /** Kort direct antwoord van 1 à 2 zinnen — voor AI-assistenten en snelle lezers. */
  answer?: string;
  blocks: Block[];
};

export type ServiceCta = { to: string; label: string; text: string };

export type BlogPost = {
  id: string;
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  category: string;
  image: string;
  imageAlt: string;
  imageVariants: DeliveryVariant[];
  ogImage: string | null;
  lead: string;
  sections: Section[];
  faq: FaqItem[];
  related: string[];
  serviceCta: ServiceCta;
  published: boolean;
  datePublished: string;
};

export type BlogSummary = Pick<BlogPost, "id" | "slug" | "title" | "metaDescription" | "excerpt" | "category" | "image" | "imageAlt" | "imageVariants" | "datePublished"> & { readingMinutes: number };

/** Categorieën die in de beheerpagina te kiezen zijn. */
export const blogCategories = [
  "Kiezen & plannen",
  "Waterbehandeling",
  "Verwarming & energie",
  "Renovatie",
  "Onderhoud",
] as const;

export const blockTypeLabels: Record<Block["type"], string> = {
  p: "Gewone tekst",
  list: "Opsomming",
  steps: "Stappen",
  table: "Tabel",
  note: "Kadertekst",
};

function blockWords(block: Block): number {
  switch (block.type) {
    case "p":
    case "note":
      return naarPlatteTekst(block.text).split(/\s+/).length;
    case "list":
      return block.items.join(" ").split(/\s+/).length;
    case "steps":
      return block.items
        .map((i) => `${i.title} ${naarPlatteTekst(i.text)}`)
        .join(" ")
        .split(/\s+/).length;
    case "table":
      return [...block.head, ...block.rows.flat()].join(" ").split(/\s+/).length;
  }
}

/** Berekent de leestijd uit de echte tekstlengte (±200 woorden per minuut). */
export function readingMinutes(post: Pick<BlogPost, "lead" | "sections" | "faq">): number {
  const words =
    naarPlatteTekst(post.lead).split(/\s+/).length +
    post.sections.reduce(
      (sum, s) =>
        sum +
        s.heading.split(/\s+/).length +
        (s.answer ? s.answer.split(/\s+/).length : 0) +
        s.blocks.reduce((bs, b) => bs + blockWords(b), 0),
      0,
    ) +
    post.faq.reduce((sum, f) => sum + `${f.q} ${f.a}`.split(/\s+/).length, 0);
  return Math.max(2, Math.round(words / 200));
}

/** BlogPosting JSON-LD voor de head() van een artikelroute. */
export function articleJsonLd(post: BlogPost, siteUrl: string) {
  const url = `${siteUrl}/blog/${post.slug}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.metaDescription,
      datePublished: post.datePublished,
      dateModified: post.datePublished,
      inLanguage: "nl-BE",
      mainEntityOfPage: url,
      url,
      author: { "@id": `${siteUrl}/#organization` },
      publisher: { "@id": `${siteUrl}/#organization` },
      articleSection: post.category,
    }),
  };
}

/** Kruimelpad Home > Blog > artikel. */
export function blogBreadcrumbJsonLd(post: BlogPost, siteUrl: string) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${siteUrl}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: `${siteUrl}/blog/${post.slug}` },
      ],
    }),
  };
}

/** Zet een titel om naar een URL-vriendelijke slug. */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function emptyPost(): BlogPost {
  return {
    id: "",
    slug: "",
    title: "",
    metaTitle: "",
    metaDescription: "",
    excerpt: "",
    category: blogCategories[0],
    image: "",
    imageAlt: "",
    imageVariants: [],
    ogImage: null,
    lead: "",
    sections: [{ heading: "", answer: "", blocks: [{ type: "p", text: "" }] }],
    faq: [],
    related: [],
    serviceCta: { to: "/offerte", label: "Vraag een offerte aan", text: "" },
    published: false,
    datePublished: siteDay(),
  };
}
